import { getSalesId, getSalesValue } from '../hooks/useSalesApiResource';

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asText = (value) => String(value ?? '').trim();

export const CUSTOM_OPTION_CATEGORY_ID = 999;
export const CUSTOM_OPTION_CATEGORY_CODE = '999';

export const isCustomWriteInCategory = (category) => {
  const normalizedCategory = category || {};
  return asNumber(normalizedCategory.id) === CUSTOM_OPTION_CATEGORY_ID
    || asText(normalizedCategory.code) === CUSTOM_OPTION_CATEGORY_CODE;
};

export const filterAssignableStationParts = (parts = [], categories = []) => {
  const customCategoryIds = new Set(
    categories.filter(isCustomWriteInCategory).map((category) => asNumber(category.id))
  );
  return parts.filter((part) => (
    asNumber(part.categoryId) !== CUSTOM_OPTION_CATEGORY_ID
    && !customCategoryIds.has(asNumber(part.categoryId))
  ));
};

export const normalizeStationPartCategory = (row = {}) => ({
  id: asNumber(getSalesId(row)),
  code: asText(getSalesValue(row, 'code', 'Code')),
  name: asText(getSalesValue(row, 'name', 'Name')),
  description: asText(getSalesValue(row, 'description', 'Description')),
  displayOrder: asNumber(getSalesValue(row, 'displayOrder', 'DisplayOrder')),
  isActive: Boolean(getSalesValue(row, 'isActive', 'IsActive') ?? true),
});

export const normalizeStationPart = (row = {}) => ({
  id: asNumber(getSalesId(row)),
  categoryId: asNumber(getSalesValue(row, 'categoryId', 'CategoryId')),
  partNumber: asText(getSalesValue(row, 'partNumber', 'PartNumber')),
  name: asText(getSalesValue(row, 'name', 'Name')),
  listPrice: asNumber(getSalesValue(row, 'listPrice', 'ListPrice')),
  sortOrder: asNumber(getSalesValue(row, 'sortOrder', 'SortOrder')),
});

export const normalizeStationPartAssignment = (row = {}) => ({
  partId: asNumber(getSalesValue(row, 'partId', 'PartId')),
  stationId: asNumber(getSalesValue(row, 'stationId', 'StationId')),
});

export const buildPartStationPayload = (partId, stationId) => ({
  PartId: asNumber(partId),
  StationId: asNumber(stationId),
});

export const buildStationPartCategoryRows = (
  categories = [],
  parts = [],
  assignments = [],
  stationId = 0
) => {
  const numericStationId = asNumber(stationId);
  const assignedPartIds = new Set(
    assignments
      .filter((assignment) => !assignment.stationId || assignment.stationId === numericStationId)
      .map((assignment) => assignment.partId)
  );
  const assignedPartsByCategory = new Map();

  parts.forEach((part) => {
    if (!assignedPartIds.has(part.id)) return;
    const current = assignedPartsByCategory.get(part.categoryId) || [];
    current.push(part);
    assignedPartsByCategory.set(part.categoryId, current);
  });

  const compareParts = (left, right) => (
    left.sortOrder - right.sortOrder
    || left.partNumber.localeCompare(right.partNumber, undefined, { numeric: true })
    || left.name.localeCompare(right.name)
  );

  const rows = categories.map((category) => ({
    ...category,
    activeParts: [...(assignedPartsByCategory.get(category.id) || [])].sort(compareParts),
  }));
  const knownCategoryIds = new Set(categories.map((category) => category.id));
  const uncategorized = parts
    .filter((part) => assignedPartIds.has(part.id) && !knownCategoryIds.has(part.categoryId))
    .sort(compareParts);

  if (uncategorized.length > 0) {
    rows.push({
      id: 0,
      code: '',
      name: 'Uncategorized',
      description: 'Assigned parts whose category is unavailable',
      displayOrder: Number.MAX_SAFE_INTEGER,
      isActive: true,
      activeParts: uncategorized,
    });
  }

  return rows.sort((left, right) => (
    left.displayOrder - right.displayOrder
    || left.code.localeCompare(right.code, undefined, { numeric: true })
    || left.name.localeCompare(right.name)
  ));
};

export const getStationPartAssignmentChanges = (currentIds, nextIds) => {
  const current = currentIds instanceof Set ? currentIds : new Set(currentIds || []);
  const next = nextIds instanceof Set ? nextIds : new Set(nextIds || []);
  return {
    add: [...next].filter((partId) => !current.has(partId)).sort((left, right) => left - right),
    remove: [...current].filter((partId) => !next.has(partId)).sort((left, right) => left - right),
  };
};

export const getBlockedStationPartRemovalIds = (removalIds = [], partMatchRules = [], stationId = null) => {
  const removals = new Set((removalIds || []).map((partId) => asNumber(partId)));
  const numericStationId = stationId === null || stationId === undefined || stationId === ''
    ? null
    : asNumber(stationId);
  return [...new Set(
    (partMatchRules || [])
      .filter((rule) => (
        numericStationId === null
        || !asNumber(rule.stationId)
        || asNumber(rule.stationId) === numericStationId
      ))
      .map((rule) => asNumber(rule.partId))
      .filter((partId) => partId && removals.has(partId))
  )].sort((left, right) => left - right);
};
